<template>
  <div
    class="myyja-form-floating"
    :class="{
      show: ready && !shouldClose,
      'is-error': error,
      'is-industry': is_industry,
    }"
    data-jscroll="true"
  >
    <div class="content-wrapper container">
      <div class="close-icon" @click="close">
        <i class="fal fa-times"></i>
      </div>
      <h3 class="text-center" v-if="!getSeller && locale !== 'sv'">
        {{
          "Hei! Kerro, Missä viljelet, niin näytämme sinulle tarjolla olevat tuotteet."
        }}
      </h3>
      <h3 v-else-if="!is_industry && (locale === 'sv' || getSeller)">
        {{ t("questionsTitle") }}
      </h3>
      <AutocompleteInput
        v-if="!getSeller && locale !== 'sv'"
        key="oma-myyja"
        :loading="loading"
        :error="error"
        placeholder="Tuotteen toimitusosoite"
        @selected="getAddress"
      />
      <YourSeller />
    </div>
  </div>
</template>
<script>
/* eslint  no-undef: 0 */
import { get, promiseTimeout, useMediaQuery, useTimeout } from "@vueuse/core";
import {
  FETCH_SWEDISH_SELLER,
  FETCH_TEOLLISUUS_SELLER,
  SET_ADDRESS,
  SET_SELLER,
} from "@/store/constants";
import { mapActions, mapGetters, mapMutations, mapState, useStore } from "vuex";
import YourSeller from "@/shared/components/YourSeller";
import { getAddressInfo } from "@/shared/helpers";
import { UPDATE_PROFILE } from "@/store/constants";
import AutocompleteInput from "../../shared/components/AutocompleteInput";
import { useI18n } from "vue-i18n";
import { onMounted } from "vue";

export default {
  name: "FloatingForm",
  components: { AutocompleteInput, YourSeller },
  emits: ["close"],
  setup() {
    const store = useStore();
    const { locale, t } = useI18n();
    onMounted(() => {
      if (locale.value === "sv") {
        store.dispatch(FETCH_SWEDISH_SELLER);
      }
      if (store.getters.getFactory) {
        store.dispatch(FETCH_TEOLLISUUS_SELLER);
      }
    });
    const { ready } = useTimeout(25);
    const isLaptop = useMediaQuery(
      "(max-width: 1023px) and (min-width: 768px)"
    );
    const isLargeScreen = useMediaQuery("(min-width: 1024px)");
    const isMobile = useMediaQuery("(max-width: 767px)");
    const isSmallScreen = useMediaQuery("(max-width: 440px)");
    return {
      ready,
      locale,
      t,
      isLaptop,
      isLargeScreen,
      isMobile,
      isSmallScreen,
    };
  },
  data() {
    return {
      error: false,
      loading: false,
      shouldClose: false,
    };
  },
  computed: {
    ...mapGetters(["getSeller"]),
    ...mapState(["is_industry"]),
    formHeight: function () {
      let baseHeight = 365;

      if (this.is_industry) {
        baseHeight = 460;
        if (this.isLaptop) return `${baseHeight}px`;
        if (this.isLargeScreen) return `${baseHeight}px`;
        return `${baseHeight - 40}px`;
      }
      if (this.getSeller.length > 1) {
        baseHeight = baseHeight + 115 * this.getSeller.length;
      }

      if (this.isSmallScreen) return `${baseHeight + 40}px`;
      if (this.isMobile) return `${baseHeight - 35}px`;
      if (this.isLaptop) return `${baseHeight + 15}px`;
      if (this.isLargeScreen) return `${baseHeight + 15}px`;

      return `${baseHeight}px`;
    },
  },
  methods: {
    ...mapMutations({
      setAddress: SET_ADDRESS,
      setSeller: SET_SELLER,
    }),
    ...mapActions({
      updateProfile: UPDATE_PROFILE,
    }),
    close: async function () {
      this.shouldClose = true;
      await promiseTimeout(200);
      this.$emit("close");
    },
    getAddress: async function ({ fromEvent, input, autocomplete }) {
      this.error = false;
      this.loading = true;
      const auto = get(autocomplete);
      const place = auto.getPlace();
      const inEl = get(input);

      const locationObject = await getAddressInfo(place, inEl, fromEvent);

      const { placeObject } = locationObject;

      if (!placeObject || (!placeObject.postal_code && !placeObject.locality)) {
        this.error = true;
        this.loading = false;
        return;
      }

      if (inEl.value) placeObject.addressLine = inEl.value;

      placeObject.geometryLocation = locationObject.geometryLocation;

      const updateResult = await this.updateProfile({
        placeObject,
        ignoreSellerError: false,
      });

      if (!updateResult) {
        this.error = true;
        this.loading = false;
        return;
      }

      this.loading = false;
    },
  },
};
</script>
<style lang="scss" scoped>
.myyja-form-floating {
  position: absolute;
  right: 0.5rem;
  top: 0;
  z-index: 8;
  background-color: var(--bs-yellow);
  padding: 2rem 2rem 2.5rem;
  transition: all 0.3s;
  border-radius: 4rem;
  width: 100px;
  height: 40px;
  overflow: hidden;

  &.show {
    width: 100%;
    height: v-bind(formHeight);
    border-radius: 0;
    right: 0;
    top: -15px;

    &.is-error {
      height: v-bind(formHeight);
    }

    @media screen and (min-width: 1024px) {
      width: 50%;
      right: 50%;
      transform: translateX(50%);
      top: 20px;
    }

    .content-wrapper {
      opacity: 1;
    }
  }

  .content-wrapper {
    opacity: 0;
    transition: opacity 0.3s;
    position: relative;
    overflow: hidden;
    white-space: nowrap;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1rem 0;

    .close-icon {
      position: absolute;
      top: 0.4rem;
      right: 2rem;
      cursor: pointer;

      i {
        font-size: 28px;
      }
    }

    .autocomplete-input {
      display: flex;
      justify-content: center;
      width: 100%;

      input {
        width: 100%;
        border: 0;
        border-radius: 0.25rem 0 0 0.25rem;
        padding: 1rem 2rem;
        font-size: 20px;
      }

      p.error {
        white-space: break-spaces;
        font-size: 16px;
        margin: 0.5rem 0 0;
        color: #d4662d;
      }

      button {
        padding: 0.75rem 2rem;
        align-self: flex-start;
        border-color: var(--bs-green);
        background-color: var(--bs-green);
        border-top-right-radius: 0.25rem;
        border-bottom-right-radius: 0.25rem;

        i {
          font-size: 32px;

          &.loading-icon {
            animation: spin 2000ms infinite linear;
            @keyframes spin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }
          }
        }
      }
    }

    h3 {
      margin: 0 0 1rem;
      white-space: break-spaces;
    }

    > p {
      white-space: break-spaces;
    }
  }

  @media screen and (max-width: 768px) {
    padding: 1.5rem 1rem;
    .content-wrapper {
      .close-icon {
        top: 0.3rem;
        right: 1rem;
      }

      .autocomplete-input {
        input {
          padding: 0.75rem 1rem;
          font-size: 18px;
        }

        p.error {
          font-size: 14px;
        }

        button {
          padding: 0.6rem 1.5rem;

          i {
            font-size: 22px;
          }
        }
      }
    }
  }
}
h3 {
  font-weight: normal;
}
</style>
